
import { Coordinate } from 'ol/coordinate'
import { DeclineClf, ReasonsForDecliningOrRequestingNewEta, ScheduleChangeModel, ScheduleChangeStates, ScheduleEtaChangeType } from '../../../src/models/schedule-change-model'
import { VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model'
import { RoutingJobOptions, VoyageRoute, WniRequiredReason } from '../../../src/models/voyage-routing-model'
import {
    getNextWaypointIndex,
    getPilotPointIndeces,
    getRouteDistance,
    getRoutePropertyAverages,
} from '../../../src/utils/route-utils'
import {
    calculateTotalFuel,
    getWeatherValues,
} from '../../voyage-suggested-routes/src/helper'
import { CoordinatesUtils } from '../../../src/utils/coordinates-utils'
import { VesselPositionModel } from '../../../src/models/vessel-position-model'
import { TerminalsModel } from '../../../src/models/terminals-model'
import { VesselCLFPopupManager } from '../../vessel-clf-popup/src/vessel-clf-manager'
import { AlertType, VesselAlertsModel } from '../../../src/models/vessel-alerts-model'
import { RequestETAChangeNotificationData } from '../../../src/services/types/vessel-notifications-services.types'
import { VoyageSchedulesModel } from '../../../src/models/voyage-schedules-model'
import { VesselNotificationsService } from '../../../src/services/vessel-notifications-service'
import { SchedulesService } from '../../../src/services/schedules-service'
import { PositionsService } from '../../../src/services/positions-service'
import { VoyageImoModel } from '../../../src/models/voyage-imo-model'

const vesselIMONumber = (<any>window)["appenv"].vesselIMONumber;
const KmToNM = 1 / 1.852;
const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
const getCurrentVesselImo = () => isVesselOnCloud ? VoyageImoModel!.imo! : vesselIMONumber;
export type RouteMetricsComparison = {
    label: string
    current: string
    new: string
}

export interface EtaComparison {
    label: string
    current: string
    new: string
  }

export enum EtaMessageTypes {
    Success = "Calculation have been completed successfully",
    Failure = "We failed to calculate. Please try again."
}

export const getLastUpdatedTime = (date: string) => {
    const dateObj = new Date(date)
    const now = new Date()
    const diff = Math.abs(now.getTime() - dateObj.getTime())
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
        return `${days} day${days > 1 ? 's' : ''} ago`
    } else if (hours > 0) {
        return `${hours} hour${hours > 1 ? 's' : ''} ago`
    } else if (minutes > 0) {
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
    } else {
        return 'Just now'
    }
}

export const defaultMetricsData: RouteMetricsComparison[] = [
    {
        label: 'Bunker consumption',
        current: '-',
        new: '-',
    },
    {
        label: 'Remaining distance',
        current: '-',
        new: '-',
    },
    {
        label: 'Remaining distance pilot',
        current: '-',
        new: '-',
    },
    {
        label: 'Average speed',
        current: '-',
        new: '-',
    },
    {
        label: 'Average speed pilot',
        current: '-',
        new: '-',
    },
    {
        label: 'Max wind speed',
        current: '-',
        new: '-',
    },
    {
        label: 'Max wave height',
        current: '-',
        new: '-',
    },
    {
        label: 'Max swell height',
        current: '-',
        new: '-',
    },
]

export const getMetricsData = async (
    currentPlan: VoyagePlan | null,
    newPlan: VoyagePlan | null,
): Promise<RouteMetricsComparison[]> => {
    const metrics: RouteMetricsComparison[] = []

    const currentRoute = currentPlan?.routes[currentPlan.selectedRouteIndex]
    const newRoute = newPlan?.routes[newPlan.selectedRouteIndex]

    if (!currentRoute) {
        console.info('currentPlanSelectedRoute is not defined')
        return defaultMetricsData
    }

    const [currMaxWind, currMaxWave, currMaxSwell] = getWeatherValues(currentRoute.route_info?.weather)
    const [newMaxWind, newMaxWave, newMaxSwell] = getWeatherValues(newRoute?.route_info?.weather)

    const [currentRouteRemainingPilotData, newRouteReaminingPilotData] = await Promise.all([
        getRemainingDataToPilot(currentRoute, currentPlan?.arrivalPilotEta),
        getRemainingDataToPilot(newRoute ?? null, newPlan?.arrivalPilotEta),
    ]);

    metrics.push(
        {
            label: 'Bunker consumption',
            current: calculateTotalFuel(currentRoute.route_info?.totals),
            new: calculateTotalFuel(newRoute?.route_info?.totals),
        },
        {
            label: 'Remaining distance',
            current: getDistance(currentRoute),
            new: getDistance(newRoute),
        },
        {
            label: 'Remaining distance pilot',
            current: `${currentRouteRemainingPilotData.distanceToPilot?.toFixed(1) ?? '-'} ${currentRouteRemainingPilotData.distanceToPilot ? ' nm' : ''}`,
            new: `${newRouteReaminingPilotData.distanceToPilot?.toFixed(1) ?? '-'} ${newRouteReaminingPilotData.distanceToPilot ? ' nm' : ''}`,
        },
        {
            label: 'Average speed',
            current: getAvgSpeed(currentRoute),
            new: getAvgSpeed(newRoute),
        },
        {
            label: 'Average speed pilot',
            current: `${currentRouteRemainingPilotData.avgSpeedToPilotEta?.toFixed(1) ?? '-'} ${currentRouteRemainingPilotData.avgSpeedToPilotEta ? ' kt' : ''}`,
            new: `${newRouteReaminingPilotData.avgSpeedToPilotEta?.toFixed(1) ?? '-'} ${newRouteReaminingPilotData.avgSpeedToPilotEta ? ' kt' : ''}`,
        },
        {
            label: 'Max wind speed',
            current: currMaxWind,
            new: newMaxWind,
        },
        {
            label: 'Max wave height',
            current: currMaxWave,
            new: newMaxWave,
        },
        {
            label: 'Max swell height',
            current: currMaxSwell,
            new: newMaxSwell,
        },
    )

    return metrics
}

export const getDistance = (route?: VoyageRoute) => {
  const dist = route?.route_info?.totals?.non_eca?.distance;
  return dist != null ? (dist * KmToNM).toFixed(1) + ' nm' : '-';
};
        

const getPilotRangeDistance = (route: VoyageRoute, indices: number[]) =>
        indices.length > 1
            ? `${getRouteDistance(route, indices[0], indices[indices.length - 1]).toFixed(1)} nm`
            : '-'

export const getAvgSpeed = (route?: VoyageRoute) =>
        route?.route_info?.speed_over_ground != null
            ? (route.route_info.speed_over_ground * 3.6 * KmToNM).toFixed(1) + ' kt'
            : '-'

const getPilotAvgSpeed = (route: VoyageRoute, indices: number[]) =>
        indices.length > 1
            ? `${(getRouteDistance(route, indices[0], indices[indices.length - 1]) * 3.6).toFixed(1)} kt`
            : '-'

export const defaultEtaComparisonData: EtaComparison[] = [
    {
        label: 'ETA',
        current: '-',
        new: '-',
    },
    {
        label: 'ETA Pilot',
        current: '-',
        new: '-',
    },
]

export const getEtaComparisonData = async (
    currentPlan: VoyagePlan | null,
    newPlan: VoyagePlan | null,
    arrivalTerminalCode: string
): Promise<EtaComparison[]> => {
    const etaData: EtaComparison[] = []

    if(!currentPlan) {
        console.info('currentPlan is not defined')
        return defaultEtaComparisonData;
    }

    const [currentEta, newEta, currentPilotEta, newPilotEta] = await Promise.all([
        formatEtaDate(currentPlan.eta, arrivalTerminalCode),
        formatEtaDate(newPlan?.eta, arrivalTerminalCode),
        formatEtaDate(currentPlan.arrivalPilotEta, arrivalTerminalCode),
        formatEtaDate(newPlan?.arrivalPilotEta, arrivalTerminalCode),
    ]);

    etaData.push(
        { label: 'ETA Pilot', current: currentPilotEta, new: newPilotEta },
        { label: 'ETA Berth', current: currentEta, new: newEta },
    );

    return etaData
}

export const formatEtaDate = async (time: string | number | undefined, locationId: string | undefined) => {
    if (!time) { return ""; }

    let timeLocal = new Date(time).toISOString();
    if (locationId && !TerminalsModel!.isPseudoTerminal(locationId))
    {
        timeLocal = await TerminalsModel!.getTerminalLocaltime(locationId, timeLocal);
    }
    timeLocal = timeLocal.substring(0,16);
    const d = new Date(timeLocal);
    const day   = String(d.getDate()).padStart(2, "0");
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const month = monthNames[d.getMonth()]
    const year  = d.getFullYear();

    let hours = d.getHours();              
    const minutes = String(d.getMinutes()).padStart(2, "0");            
    const hoursStr = String(hours).padStart(2, "0");

    return `${day} ${month} ${year}, ${hoursStr}:${minutes}`;
  }

export function formatLocalDateTimeString(dateTimeStr: string | number): string {
    if (!dateTimeStr) return "";

    const date = new Date(dateTimeStr);

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const day = String(date.getDate()).padStart(2, "0");
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day} ${month} ${year}, ${hours}:${minutes}`;
}

export function formatUTCDateTimeString(dateTimeStr: string | number): string {
  if (!dateTimeStr) return "";

  const date = new Date(dateTimeStr);

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const day = date.getUTCDate(); // no padStart → 1, 2, 3 …
  const month = monthNames[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");

  return `${day} ${month} ${year}, ${hours}:${minutes} UTC`;
}

/**
 * Returns true if the given timestamp is older than 30 minutes from now.
*/
export const isOlderThan30Minutes = (timestamp: string): boolean => {
    const diffMs = Date.now() - new Date(timestamp).getTime()
    return diffMs > 30 * 60 * 1000
  }
  
/**
 * Returns true if the difference between two timestamps is at least 10 minutes.
*/
  export const has10MinuteDifference = (timestamp1: string, timestamp2: string): boolean => {
    const diffMs = Math.abs(new Date(timestamp1).getTime() - new Date(timestamp2).getTime())
    return diffMs >= 10 * 60 * 1000
  }

/**
 * Returns the routing options for the given plan and route.
 */
export const buildRoutingOptions = (
    plan: VoyagePlan | undefined,
    route: VoyageRoute,
    updateToPlan: boolean,
    updatePlanEta: boolean,
    onAccept: boolean = false,
): RoutingJobOptions => {
    // if ETA change is accepted, then we want to keep the same required reason for weather routing, if any. If it's not accepted, then we set it to manual, as the change is made by the user and not due to weather routing
    const weatherRoutingInfo = route.weatherRoutingInfo
        ? {
            ...route.weatherRoutingInfo,
            required_reason: onAccept
                ? route.weatherRoutingInfo.required_reason
                : WniRequiredReason.Manual,
            updated_at_reason: onAccept
                ? 'RTA Change'
                : 'Regular',
        }
        : undefined

    return {
        routeId: route.routeId,
        planScheduleId: plan?.scheduleId,
        updatePlan: updateToPlan ? { routeIndex: plan?.selectedRouteIndex ?? 0, autoApprove: true } : undefined,
        draftFore: plan!.foreDraft,
        draftAft: plan!.aftDraft,
        gmt: plan!.gmt !== null ? plan!.gmt : undefined,
        etd: plan!.etd,
        eta: plan!.eta,
        rollAngle: plan!.rollAngle !== null ? plan!.rollAngle : undefined,
        arrivalPilotEta: updateToPlan ? plan!.arrivalPilotEta : undefined,
        numberOfReefers: plan!.numberOfReefers,
        routeType: 'WeatherOptimized',
        whaleZonesApplied: route.route_info?.whaleZonesApplied,
        disableWhaleZoneSpeedRestrictions: route.route_info?.disableWhaleZoneSpeedRestrictions,
        passagePlan: route.passagePlan,
        updatePlanEta: updatePlanEta,
        weatherRoutingInfo: weatherRoutingInfo,
        solidGm: plan!.solidGm !== null ? plan!.solidGm : undefined,
    };
};

export const updateArrivalPilotEtaToRouteGeometry = (arrivalPilotEta: string, route: VoyageRoute) => {
    if (!arrivalPilotEta || !route.route_geometry) {
        return route;
    }
    const pilotEta = new Date(arrivalPilotEta).toISOString()

    const pilotPointIndices = getPilotPointIndeces(route);

    const arrivalPilotIndex = pilotPointIndices[pilotPointIndices.length - 1];

    route.route_geometry.forEach((point, index) => {
        if (index === arrivalPilotIndex) {
            point.properties.eta = pilotEta;
        }
    });

    return route;
}


export const getRemainingDataToPilot = async (route: VoyageRoute | null, arrivalPilotEta: number | undefined) =>{

    const positionData = VesselPositionModel!.position;

    if (!positionData || !positionData.latitude || !positionData.longitude) {
        console.log("Vessel position not available");
        return { distanceToPilot: null, timeToPilotEta: null, avgSpeedToPilotEta: null };
    }
    const position : Coordinate = [positionData.longitude, positionData.latitude]

    if (!route || !route.route_geometry || !route.route_geometry.length) {
        console.log("Route geometry not available");
        return { distanceToPilot: null, timeToPilotEta: null, avgSpeedToPilotEta: null };
    }
    const nextWayPointIndex = await getNextWaypointIndex(route.route_geometry);

    if (nextWayPointIndex === -1) {
        console.log("No next waypoint found in route");
        return { distanceToPilot: null, timeToPilotEta: null, avgSpeedToPilotEta: null };
    }
    
    const defaultResult = { distanceToPilot: null, timeToPilotEta: null, avgSpeedToPilotEta: null };
    const KmToNM = 1 / 1.852;

    const pilotIndices = getPilotPointIndeces(route);
    if (pilotIndices.length < 2) {
        console.log("No arrival pilot points found in route");
        return defaultResult;
    }

    const arrivalPointIndex = pilotIndices[pilotIndices.length-1];

    if (nextWayPointIndex == null || nextWayPointIndex === -1) {
        console.log("No next waypoint found in route");
        return defaultResult;
    }

    const nextWaypoint = route.route_geometry[nextWayPointIndex];
    const nextWaypointCoords = nextWaypoint?.geometry.type === 'Point'
    ? nextWaypoint.geometry.coordinates as number[]
    : null;

    if (!nextWaypointCoords) {
        console.log("Next waypoint coordinates not found");
        return defaultResult;
    }

    const dists = CoordinatesUtils.calcDistance(position, nextWaypointCoords);
    const incomingLeg = route.route_geometry[nextWayPointIndex - 1];
    const distanceToNextWP = incomingLeg?.properties.leg_type === "GreatCircle" ? dists.gc : dists.rl;
    const distanceFromNextWpToPilot = getRouteDistance(route, nextWayPointIndex, arrivalPointIndex) * KmToNM;
    const distanceToPilot = distanceToNextWP + distanceFromNextWpToPilot;

    const timeToPilotEta = arrivalPilotEta ? new Date(arrivalPilotEta).getTime() - Date.now() : null;
    const avgSpeedToPilotEta = timeToPilotEta ? (distanceToPilot / timeToPilotEta) * 3600000 : null;

    return { distanceToPilot, timeToPilotEta, avgSpeedToPilotEta };
}

export const constructInProgressPayload = (alert: ScheduleEtaChangeType) => {
    const data = alert.data;
    const payload = {
        plan_id: data.plan_id,
        source: "VesselVoyage",
        departure_terminal_code: data.departure_terminal_code,
        arrival_terminal_code: data.arrival_terminal_code,
        schedule_id: data.schedule_id,
        arrival_portcall_id: data.arrival_portcall_id,
        departure_portcall_id: data.departure_portcall_id
    }
    return payload;
}

export const constructAcceptPayload = async (alert: ScheduleEtaChangeType, plan: VoyagePlan, geo_timezone: string) => {    
    const currentRoute = plan.routes[plan.selectedRouteIndex]
    const [currMaxWind, currMaxWave, currMaxSwell] = getWeatherValues(currentRoute.route_info?.weather)
    const currentRouteRemainingPilotData = await getRemainingDataToPilot(currentRoute, plan.arrivalPilotEta);

    const data = alert.data;
    const payload = {
        plan_id: data.plan_id,
        source: "VesselVoyage",
        departure_terminal_code: data.departure_terminal_code,
        arrival_terminal_code: data.arrival_terminal_code,
        schedule_id: data.schedule_id,
        arrival_portcall_id: data.arrival_portcall_id,
        departure_portcall_id: data.departure_portcall_id,
        voyage_plan: {
            create_timestamp: new Date().toISOString(),
            arrivalTimings: {
                array: [
                {
                    timestamp: new Date(plan.eta).toISOString(),
                    type: "ESTIMATED"
                },
                {
                    timestamp: new Date(plan.arrivalPilotEta).toISOString(),
                    type: "ESTIMATED PILOT_BOARDING_PLACE"
                }
                ]
            },
            departureTimings: {
                array: [
                {
                    timestamp: new Date(plan.etd).toISOString(),
                    type: "ESTIMATED"
                },
                {
                    timestamp: new Date(plan.departurePilotEtd).toISOString(),
                    type: "ESTIMATED PILOT_DROP_OFF"
                }
                ]
            },
        },
        bunker_consumption_mt: calculateTotalFuel(currentRoute.route_info?.totals),
        remaining_distance_nm: getDistance(currentRoute),
        remaining_distance_pilot_nm: `${currentRouteRemainingPilotData.distanceToPilot?.toFixed(1) || '-'} nm`,
        average_speed_nms: getAvgSpeed(currentRoute),
        average_speed_pilot_nms: `${currentRouteRemainingPilotData.avgSpeedToPilotEta?.toFixed(1) || '-'} kt`,
        max_wind_speed_nms: currMaxWind,
        max_wave_height_mts: currMaxWave,
        max_swell_height_mts: currMaxSwell,
        geo_timezone: geo_timezone
    }
    return payload;
}

export const constructDeclinePayload = (alert: ScheduleEtaChangeType, declinePayload: DeclineClf, geo_timezone: string) => {
    const data = alert.data;
    const payload = {
        plan_id: data.plan_id,
        source: "VesselVoyage",
        departure_terminal_code: data.departure_terminal_code,
        arrival_terminal_code: data.arrival_terminal_code,
        schedule_id: data.schedule_id,
        arrival_portcall_id: data.arrival_portcall_id,
        departure_portcall_id: data.departure_portcall_id,
        best_ETA_pilot_station: declinePayload.bestEtaTime,
        deviation_reason: declinePayload.reason,
        deviation_reason_code: declinePayload.reasonCode,
        deviation_reason_created_by: getCurrentVesselImo(),
        deviation_remark: declinePayload.comment,
        deviation_remark_timestamp: new Date().toISOString(),
        geo_timezone: geo_timezone
    }
    return payload;
}

export const constructEndedPayload = (alert: ScheduleEtaChangeType, auto_close_remark: string) => {
    const data = alert.data;
    const payload = {
        plan_id: data.plan_id,
        source: "VesselVoyage",
        schedule_id: data.schedule_id,
        departure_terminal_code: data.departure_terminal_code,
        arrival_terminal_code: data.arrival_terminal_code,
        arrival_portcall_id: data.arrival_portcall_id,
        departure_portcall_id: data.departure_portcall_id,
        auto_close_reason: "Arrival terminal changed",
        auto_close_remark: auto_close_remark
    }
    return payload;
}

const constructEtaRequestPayload = (currentPlan:VoyagePlan, requestedPlan: VoyagePlan, etaRequestPayload: DeclineClf, arrival_portcall_id: number, departure_portcall_id: number, vesselCode: string | undefined, arrivalVoyage: string, departureVoyage: string, arrivalBerthLocalTime: string, arrivalPilotLocalTime: string) => {
    const payload = {
        plan_id: requestedPlan.id,
        source: "VesselVoyage",
        schedule_id: currentPlan.scheduleId,
        departure_terminal_code: currentPlan.departureTerminalCode,
        arrival_terminal_code: currentPlan.arrivalTerminalCode,
        arrival_portcall_id: `${arrival_portcall_id}`,
        departure_portcall_id: `${departure_portcall_id}`,
        arrival_voyage: arrivalVoyage,
        departure_voyage: departureVoyage,
        vessel_code: vesselCode ?? '',
        earliest_requested_eta: {
            arrivalTimings: {
            array: [
            {
                createSource: {
                    string: "VesselVoyagePlan"
                },
                timestamp: new Date(requestedPlan.eta).toISOString(),
                type: "ESTIMATED"
            },
            {
                createSource: {
                    string: "VesselVoyagePlan"
                },
                timestamp: new Date(requestedPlan.arrivalPilotEta).toISOString(),
                type: "ESTIMATED PILOT_BOARDING_PLACE"
            },
            {
                createSource: {
                    string: "VesselVoyagePlan"
                },
                timestamp: arrivalBerthLocalTime,
                type: "ESTIMATED_LOCAL"
            },
            {
                createSource: {
                    string: "VesselVoyagePlan"
                },
                timestamp: arrivalPilotLocalTime,
                type: "ESTIMATED PILOT_BOARDING_PLACE_LOCAL"
            }
            ]
            }
        },
        scheduled: {
        arrivalTimings: {
            array: [
            {
                createSource: {
                    string: "Voyager"
                },
                timestamp: new Date(currentPlan.eta).toISOString(),
                type: "ESTIMATED"
            },
            {
                createSource: {
                    string: "Voyager"
                },
                timestamp: new Date(currentPlan.arrivalPilotEta).toISOString(),
                type: "ESTIMATED PILOT_BOARDING_PLACE"
            }
            ]
        }
        },
        deviation_reason: etaRequestPayload.reason,
        deviation_reason_code: etaRequestPayload.reasonCode,
        deviation_reason_created_by: "JCH471",
        deviation_remark: etaRequestPayload.comment,
        deviation_remark_timestamp: new Date().toISOString()
    }

    return payload;
}

export const postNotificationState = async (state: string, plan?: VoyagePlan | null, declinePayload?: DeclineClf | null): Promise<void> => {
    const alert = await ScheduleChangeModel!.getCurrentAlert();
    const geo_timezone = await PositionsService.getCurrentTimeZone(getCurrentVesselImo());
    if(!alert)
        return;
    switch (state){
        case ScheduleChangeStates.InProgress: 
            {
                const payload = constructInProgressPayload(alert)
                await ScheduleChangeModel!.postStateChangeAlert(alert.id, ScheduleChangeStates.InProgress, payload);
                break;
            }
        case ScheduleChangeStates.Accepted:
            {  
                const payload = await constructAcceptPayload(alert, plan!, geo_timezone ?? '');
                await ScheduleChangeModel!.postStateChangeAlert(alert.id, ScheduleChangeStates.Accepted, payload);
                break;
            }
        case ScheduleChangeStates.Declined:
            {
                if(declinePayload){
                    const payload = constructDeclinePayload(alert, declinePayload, geo_timezone ?? '');
                    await ScheduleChangeModel!.postStateChangeAlert(alert.id, ScheduleChangeStates.Declined, payload);
                }
                break;
            }
    }
    
}

export const postEtaChangeRequest = async (requestedPlan: VoyagePlan | null, requestPayload: DeclineClf) => {
    const plan = await VesselPlanningModel!.getExecutingPlan();
    const activeSchedule = await VoyageSchedulesModel!.getActiveSchedule(getCurrentVesselImo());
    const vesselCode = activeSchedule?.arrivalPortCall?.GSIS?.vesselCode;
    const arrival_portcall_id = activeSchedule?.arrivalPortCall?.GSIS?.gsisId || 0;
    const departure_portcall_id = activeSchedule?.departurePortCall?.GSIS?.gsisId || 0;
    const arrivalVoyage = activeSchedule?.arrivalPortCall?.GSIS?.arrivalVoyage || '';
    const departureVoyage = activeSchedule?.arrivalPortCall?.GSIS?.departureVoyage || '';
    const arrivalPilotLocalTime = await convertUTCToLocal(requestedPlan?.arrivalPilotEta, plan?.arrivalTerminalCode);
    const arrivalBerthLocalTime = await convertUTCToLocal(requestedPlan?.eta, plan?.arrivalTerminalCode);
    if(!requestedPlan || !plan)
        return;
    const payload = constructEtaRequestPayload(plan, requestedPlan, requestPayload, arrival_portcall_id, departure_portcall_id, vesselCode, arrivalVoyage, departureVoyage, arrivalBerthLocalTime, arrivalPilotLocalTime);
    await ScheduleChangeModel!.postEtaRequestAlert(payload);
}

export const getPlanWithNewEta = (currentPlan: VoyagePlan, arrivalBerth: string | undefined, arrivalPilotBerth: string | undefined) => {
        const arrivalberthTimestamp = new Date(arrivalBerth ?? '').getTime();
        const arrivalPilotTimestamp = new Date(arrivalPilotBerth ?? '').getTime();
        const newPlan: VoyagePlan = JSON.parse(JSON.stringify(currentPlan)); // deep clone

        newPlan.eta = arrivalberthTimestamp;
        newPlan.arrivalPilotEta = arrivalPilotTimestamp;

        return newPlan;
}

export async function showDeclineClfAndPostState(
  eta: string,
  alertType: AlertType,
): Promise<DeclineClf | null> {
  const dialog = VesselCLFPopupManager.createClf({
    headerText: `Reason for Decline ETA Change Request to ${eta} LT`,
    bodyText: "As you declined ETA changes please select a reason from the below list.",
    reasonOptions: ReasonsForDecliningOrRequestingNewEta,
    hasDateInput: true,
  });

  const response = await dialog.getResponse();

  if (response.status === "cancel") {
    return null;
  }

  const declinePayload: DeclineClf = {
    reason: response.reason,
    reasonCode: response.reasonCode,
    comment: response.comment,
    bestEtaTime: response.date,
  };

  VesselAlertsModel!.addAlert(getCurrentVesselImo(), {
    title: 'Successfully declined',
    message: 'We informed TBMs of your reason for rejecting the ETA change request and best ETA and shared best ETA pilot station with them. Please await their response.',
    id: `${eta}`,
    type: alertType,
  });
  
  return declinePayload;
}

export async function closeEtaNotificationOnTerminalChange(origArrivalTerminal: string | null , arrivalTerminal: string){
    if(origArrivalTerminal === null || origArrivalTerminal !== arrivalTerminal){
        //if terminal has been changed need to close the eta notification
        const latestAlert = await ScheduleChangeModel!.getLatestAlert();
        const auto_close_remark = origArrivalTerminal === null ? "Port Inducement" : "Port Change"
        const payload = constructEndedPayload(latestAlert, auto_close_remark);
        await VesselNotificationsService.postScheduleChangeAlerts(latestAlert.id, ScheduleChangeStates.Ended, payload);
    }
}

export async function showEtaRequestClf(
    eta: string,
    alertType: AlertType,
    etaBerth: string,
    etaPilot: string
): Promise<DeclineClf | null> {
    const dialog = VesselCLFPopupManager.createClf({
        headerText: `Reason for ETA Change Request to ${eta} LT`,
        bodyText: "As you changed ETA, please select a reason from the below list.",
        reasonOptions: ReasonsForDecliningOrRequestingNewEta,
        etaBerth: etaBerth,
        etaPilot: etaPilot,
    });

    const response = await dialog.getResponse();

    if (response.status === "cancel") {
        return null;
    }

    const etaRequestPayload: DeclineClf = {
        reason: response.reason,
        reasonCode: response.reasonCode,
        comment: response.comment,
    };

    VesselAlertsModel!.addAlert(getCurrentVesselImo(), {
        title: 'Requested new ETA',
        message: 'We informed TBMs of your reason for the ETA change request and earliest ETA. Please await their response.',
        id: `${eta}`,
        type: alertType,
    });
    
    return etaRequestPayload;
}

export async function checkIfPilotPointisPassed(schedule_id: string, showEtaRequest: boolean = true): Promise<boolean> {
    const currentPlan = await VesselPlanningModel!.getCurrentPlan(schedule_id);
    if( !currentPlan) return showEtaRequest;
    const route = currentPlan.routes[currentPlan.selectedRouteIndex];
    const pilotIndeces = getPilotPointIndeces(route);
    const arrivalPilotIndex = pilotIndeces[pilotIndeces.length-1];
    const currentLeg = route.route_geometry[arrivalPilotIndex];
    if (currentLeg && currentLeg.properties.isPassed){
        return false;     
    }
    return showEtaRequest;
}

export function getParsedData(
    d: string | RequestETAChangeNotificationData
): RequestETAChangeNotificationData {
    return (typeof d === 'string' ? JSON.parse(d) : d) as RequestETAChangeNotificationData;
}

export async function convertLocalToUTC(
  date: string,
  terminal: string | undefined,
  defaultTime?: string
): Promise<string> {
  // Guard against empty input
  if (!date) return defaultTime ?? '';

  // Normalize to ISO-like format (with seconds)
  const parsedDateString = date.substring(0, 16) + ':00';

  if (terminal && !TerminalsModel!.isPseudoTerminal(terminal)) {
    // Convert from terminal local time to UTC
    const etaUtc = await TerminalsModel!.getUTCFromTerminalLocaltime(
      terminal,
      parsedDateString
    );
    return etaUtc || defaultTime || '';
  } else {
    // Use UTC directly when no valid terminal is provided
    return new Date(parsedDateString + 'Z').toISOString();
  }
}

export async function convertUTCToLocal(
  date: string | number | undefined,
  terminal: string | undefined,
): Promise<string> {
    if (!date) return '';

    let timeLocal = new Date(date).toISOString();
    if (terminal && !TerminalsModel!.isPseudoTerminal(terminal))
    {
        timeLocal = await TerminalsModel!.getTerminalLocaltime(terminal, timeLocal);
    }
    return timeLocal;
}

export const constructEndedPayloadForEtaRequest = (alert: RequestETAChangeNotificationData) => {
   const payload = {
        plan_id: alert.plan_id,
        source: "VesselVoyage",
        schedule_id: alert.schedule_id,
        departure_terminal_code: alert.departure_terminal_code,
        arrival_terminal_code: alert.arrival_terminal_code,
        arrival_portcall_id: alert.arrival_portcall_id,
        departure_portcall_id: alert.departure_portcall_id,
        auto_close_reason: "New ETA requested by captain",
        auto_close_reason_metadata: {
            new_eta_berth: "",
            new_eta_pilot: "",
            correlation_id: ""
        },
        auto_close_remark: ""
    }
    return payload;
}

export const stateDialogs: Record<string, { header: string; body: string }> = {
    [ScheduleChangeStates.Accepted]: {
        header: 'The Schedule Change Request by TBMs has already been Accepted',
        body: 'Plan has been updated with the new ETA and speed profiles.'
    },
    [ScheduleChangeStates.Declined]: {
        header: 'The Schedule Change Request by TBMs has already been Declined',
        body: 'Other user has declined it, you can accept it from schedule card.'
    },
    [ScheduleChangeStates.Ended]: {
        header: 'Schedule Change Request No Longer Active',
        body: 'This request has already been closed in the backend and is no longer available for action. The view will refresh automatically every 3 minutes to limit bandwidth usage.'
    }
};

export const getRemainingDistanceFromCurrentWP = async (route: VoyageRoute) =>{

    const positionData = VesselPositionModel!.position;

    if (!positionData || !positionData.latitude || !positionData.longitude) {
        console.log("Vessel position not available");
        return '-'
    }
    const position : Coordinate = [positionData.longitude, positionData.latitude]

    if (!route || !route.route_geometry || !route.route_geometry.length) {
        console.log("Route geometry not available on view route");
        return '-'
    }
    const nextWayPointIndex = await getNextWaypointIndex(route.route_geometry);

    if (nextWayPointIndex === -1) {
        console.log("No next waypoint found in route");
        return '-';
    }
    
    const defaultResult = '-';
    const KmToNM = 1 / 1.852;

    if (nextWayPointIndex == null || nextWayPointIndex === -1) {
        console.log("No next waypoint found in route");
        return defaultResult;
    }

    const nextWaypoint = route.route_geometry[nextWayPointIndex];
    const nextWaypointCoords = nextWaypoint?.geometry.type === 'Point'
    ? nextWaypoint.geometry.coordinates as number[]
    : null;

    const lastWaypointIndex = route.route_geometry.length - 1;
    if (!nextWaypointCoords) {
        console.log("Next waypoint coordinates not found");
        return defaultResult;
    }

    const dists = CoordinatesUtils.calcDistance(position, nextWaypointCoords);
    const incomingLeg = route.route_geometry[nextWayPointIndex - 1];
    const distanceToNextWP = incomingLeg?.properties.leg_type === "GreatCircle" ? dists.gc : dists.rl;
    const distanceFromNextWpToLastWp = getRouteDistance(route, nextWayPointIndex, lastWaypointIndex) * KmToNM;
    const remainingDistance = distanceToNextWP + distanceFromNextWpToLastWp;

    return remainingDistance.toFixed(1) + ' nm';
}

export const getTotalDistance = (route: VoyageRoute) => {
    //returns the total distance of whole voyage
    const KmToNM = 1 / 1.852;
    const firstWaypointIndex = 0;
    const lastWaypointIndex = route.route_geometry.length - 1;
    const totalDistance = getRouteDistance(route, firstWaypointIndex, lastWaypointIndex) * KmToNM;

    return totalDistance.toFixed(1) + ' nm';
}
